/**
 * Strips JSON-LD metadata (@id, @type, @context) from decoded API responses.
 *
 * Google bot crawls SSR HTML and discovers API URLs from JSON-LD fields
 * like `/api/v2/.well-known/genid/...` and `/api/v2/shop/product-variants/.../expand`.
 *
 * Two-step approach:
 * 1. Original schema decodes first (transforms like HydraView page extraction use @id).
 * 2. AST-rewritten target schema + runtime `deepStripJsonLd` remove @id from output.
 *
 * `SchemaAST.toType(self.ast)` resolves all encoding transformations to the type-side
 * AST; `stripJsonLdFromAST` then removes @id/@type/@context property signatures so the
 * target type no longer requires them. Fixes the HydraCollectionResponse required-@id issue.
 *
 * v4 AST nodes differ from v3: `TypeLiteral`→`Objects`, `TupleType`→`Arrays`, and the
 * `Refinement`/`Transformation` AST classes no longer exist — refinements live on
 * `Base.checks`, transformations on `Base.encoding`. Walking those branches is unnecessary
 * because `toType` strips them from the AST before we ever see it.
 */

import type { Chunk } from 'effect';

import { Schema, SchemaAST, SchemaGetter } from 'effect';
import * as C from 'effect/Chunk';

const JSON_LD_KEYS = new Set<PropertyKey>(['@id', '@type', '@context']);

// ─── Type-level stripping ────────────────────────────────────────────────────

// Homomorphic mapped type using TS's key-remapping `as` clause.
// `[K in keyof T as Exclude<K, '@id' | '@type' | '@context'>]` iterates over
// `keyof T` directly (keeps the type homomorphic, preserves `readonly` and `?`
// modifiers) and remaps each key by removing the JSON-LD names.
//
// Works for both schema shapes the openapi-gen produces:
//
// - `Schema.StructWithRest(...)` types whose `keyof T` includes `string` (the
//   index signature). `Exclude<string, '@id' | …> = string` (literal types are
//   excluded, `string` itself is not), so the index signature survives and the
//   named '@id'/'@type'/'@context' keys are dropped. Other named keys flow
//   through with their modifiers intact.
// - Plain `Schema.Struct({...})` types with no index signature: the named keys
//   that are NOT JSON-LD pass through unchanged (modifiers preserved by the
//   homomorphism).
//
// The runtime `deepStripJsonLd` below strips the same three keys at runtime —
// this type only mirrors that intent at the static layer.

export type StripJsonLd<T> = T extends readonly (infer E)[]
  ? readonly StripJsonLd<E>[]
  : T extends Chunk.Chunk<infer E>
    ? Chunk.Chunk<StripJsonLd<E>>
    : T extends { readonly _op: string }
      ? T // Preserve Effect data types (Option, Either, etc.)
      : // Preserve primitive leaves as-is. Critical for BRANDED primitives
        // (`string & Brand<'X'>`, `number & Brand<'X'>`): the `Brand` phantom is
        // an object-shaped intersection member, so a branded primitive otherwise
        // matches the `T extends object` branch below and gets mangled into a
        // structural object (losing the brand and the primitive). A branded
        // primitive is still assignable to its base primitive, so this branch
        // catches it and returns it untouched — matching the runtime
        // `deepStripJsonLd`, which never rewrites primitive leaves.
        T extends string | number | boolean | bigint | symbol | null | undefined
        ? T
        : T extends object
          ? { [K in keyof T as Exclude<K, '@id' | '@type' | '@context'>]: StripJsonLd<T[K]> }
          : T;

// ─── Runtime deep strip ─────────────────────────────────────────────────────

const isPlainObject = (value: object): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

export const deepStripJsonLd = <T>(data: T): StripJsonLd<T> => {
  if (Array.isArray(data)) {
    return data.map((v: unknown) => deepStripJsonLd(v)) as StripJsonLd<T>;
  }
  if (data !== null && typeof data === 'object') {
    if (C.isChunk(data)) {
      return C.map(data, (a) => deepStripJsonLd(a)) as StripJsonLd<T>;
    }
    if (isPlainObject(data)) {
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        if (JSON_LD_KEYS.has(key)) continue;
        result[key] = deepStripJsonLd(value);
      }
      return result as StripJsonLd<T>;
    }
  }
  return data as StripJsonLd<T>;
};

// ─── AST rewriting (v4) ─────────────────────────────────────────────────────

const stripJsonLdFromAST = (ast: SchemaAST.AST): SchemaAST.AST => {
  switch (ast._tag) {
    case 'Objects': {
      const filteredProps = ast.propertySignatures.filter((ps) => !JSON_LD_KEYS.has(ps.name));
      const processedProps = filteredProps.map(
        (ps) => new SchemaAST.PropertySignature(ps.name, stripJsonLdFromAST(ps.type))
      );
      const processedIndexSigs = ast.indexSignatures.map(
        (is) => new SchemaAST.IndexSignature(is.parameter, stripJsonLdFromAST(is.type))
      );
      return new SchemaAST.Objects(
        processedProps,
        processedIndexSigs,
        ast.annotations,
        ast.checks,
        ast.encoding,
        ast.context
      );
    }

    case 'Union':
      return new SchemaAST.Union(
        ast.types.map((t) => stripJsonLdFromAST(t)),
        ast.options,
        ast.annotations,
        ast.checks,
        ast.encoding,
        ast.context,
        ast.encodingChecks
      );

    case 'Arrays':
      return new SchemaAST.Arrays(
        ast.isMutable,
        ast.elements.map((e) => stripJsonLdFromAST(e)),
        ast.rest.map((r) => stripJsonLdFromAST(r)),
        ast.annotations,
        ast.checks,
        ast.encoding,
        ast.context
      );

    case 'Suspend':
      return new SchemaAST.Suspend(
        () => stripJsonLdFromAST(ast.thunk()),
        ast.annotations,
        ast.checks,
        ast.encoding,
        ast.context
      );

    // Declaration's `run` produces a parser closed over its type parameters; we
    // recurse into the type parameters so child Objects/Arrays get rewritten,
    // but the parser itself stays untouched (it returns the original type).
    case 'Declaration':
      return new SchemaAST.Declaration(
        ast.typeParameters.map((tp) => stripJsonLdFromAST(tp)),
        ast.run,
        ast.annotations,
        ast.checks,
        ast.encoding,
        ast.context
      );

    default:
      return ast;
  }
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Wraps a schema to strip @id, @type, @context from decoded output.
 *
 * The original schema decodes first (so transforms like HydraView page extraction
 * can use @id), then deepStripJsonLd removes JSON-LD fields from the result.
 *
 * The target schema is AST-rewritten so the resulting `Type` no longer carries
 * @id/@type/@context — required for schemas like HydraCollectionResponse where
 * @id is non-optional on the wire side.
 *
 * @example
 * ```ts
 * HttpClientResponse.schemaBodyJson(withoutJsonLd(ProductShow))(response)
 * HttpClientResponse.schemaBodyJson(withoutJsonLd(HydraCollectionResponse(ProductBase)))(response)
 * ```
 */
export const withoutJsonLd = <S extends Schema.Top>(
  self: S
): Schema.Codec<
  StripJsonLd<S['Type']>,
  S['Encoded'],
  S['DecodingServices'],
  S['EncodingServices']
> => {
  const typeAST = SchemaAST.toType(self.ast);
  const cleanAST = stripJsonLdFromAST(typeAST);
  const target = Schema.make<Schema.Codec<StripJsonLd<S['Type']>>>(cleanAST);

  return self.pipe(
    Schema.decodeTo(target, {
      decode: SchemaGetter.transform((a: S['Type']) => deepStripJsonLd(a)),
      encode: SchemaGetter.passthrough<S['Type'], StripJsonLd<S['Type']>>({ strict: false }),
    })
  );
};
